import React, { useState, useEffect, useContext } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import { useSettings, useSettingsSchema, useSettingsMutations } from '../../../hooks/console/useConsoleAPI';
import { AuthContext } from '../../../../../../context/AuthContext';

const GROUP_LABELS = {
    ranking:       '검색 순위 가중치',
    training:      '교육 가점 설정',
    penalty:       '벌칙 처리 설정',
    qualification: '자격 일치 설정',
    exception:     '예외 처리 설정',
    custom:        '기타 설정',
};

export default function SettingsTab() {
    const { auth } = useContext(AuthContext);
    const { data, isLoading } = useSettings();
    const { data: schemaData } = useSettingsSchema();
    const { update, reload } = useSettingsMutations();

    const items = data?.items ?? [];

    // 스키마 맵: setting_key → { min_value, max_value, step, ui_type }
    const schemaMap = {};
    (schemaData?.groups ?? []).forEach((g) => {
        (g.items ?? []).forEach((s) => {
            schemaMap[s.setting_key] = s;
        });
    });

    const [edits, setEdits] = useState({});
    const [updatedBy, setUpdatedBy] = useState(auth?.userId ?? '');

    useEffect(() => {
        const init = {};
        items.forEach((s) => { init[s.setting_key] = s.setting_value; });
        setEdits(init);
    }, [data]);

    useEffect(() => {
        if (auth?.userId) setUpdatedBy(auth.userId);
    }, [auth?.userId]);

    const groups = items.reduce((acc, s) => {
        if (!acc[s.setting_group]) acc[s.setting_group] = [];
        acc[s.setting_group].push(s);
        return acc;
    }, {});

    const hasChanges = items.some((s) => edits[s.setting_key] !== s.setting_value);

    const handleSave = () => {
        if (!updatedBy) return;
        const changed = items
            .filter((s) => edits[s.setting_key] !== s.setting_value)
            .map((s) => ({ setting_key: s.setting_key, setting_value: edits[s.setting_key] }));
        if (!changed.length) return;
        update.mutate({ settings: changed, updated_by: updatedBy });
    };

    const handleReload = () => {
        if (!window.confirm('현재 설정으로 엔진을 재로드하시겠습니까?')) return;
        reload.mutate({ requested_by: 'admin' });
    };

    const renderInput = (s) => {
        const schema = schemaMap[s.setting_key] ?? {};
        const uiType = schema.ui_type ?? (s.setting_type === 'bool' ? 'switch' : s.setting_type === 'float' || s.setting_type === 'int' ? 'input' : 'input');
        const val = edits[s.setting_key] ?? s.setting_value;
        const onChange = (v) => setEdits((prev) => ({ ...prev, [s.setting_key]: v }));
        const changed = val !== s.setting_value;

        if (uiType === 'switch' || s.setting_type === 'bool') {
            return (
                <label className="relative inline-flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                        type="checkbox"
                        className="sr-only"
                        checked={val === 'true'}
                        onChange={() => onChange(val === 'true' ? 'false' : 'true')}
                    />
                    <div
                        style={{ width: '2.5rem', height: '1.25rem' }}
                        className={`relative rounded-full transition-colors shrink-0 ${val === 'true' ? 'bg-sb-primary' : 'bg-gray-300'}`}
                    >
                        <span
                            style={{ width: '1rem', height: '1rem', top: '0.125rem' }}
                            className={`absolute rounded-full bg-white shadow transition-transform ${val === 'true' ? 'translate-x-5' : 'translate-x-0.5'}`}
                        />
                    </div>
                    <span className="text-base text-gray-600 w-10 shrink-0">
                        {val === 'true' ? '활성' : '비활성'}
                    </span>
                    {changed && (
                        <span className="text-sm text-gray-400 shrink-0">
                            (기본: {s.default_value === 'true' ? '활성' : '비활성'})
                        </span>
                    )}
                </label>
            );
        }

        if (uiType === 'slider') {
            const min = schema.min_value ?? 0;
            const max = schema.max_value ?? 10;
            const step = schema.step ?? (s.setting_type === 'float' ? 0.01 : 1);
            return (
                <div className="flex items-center gap-2 w-56">
                    <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={val}
                        onChange={(e) => onChange(e.target.value)}
                        className="flex-1 accent-sb-primary"
                    />
                    <span className={`text-base font-mono w-12 text-right shrink-0 ${changed ? 'text-amber-600 font-semibold' : 'text-gray-700'}`}>
                        {val}
                    </span>
                    {changed && (
                        <span className="text-sm text-gray-400 whitespace-nowrap shrink-0">
                            / {s.default_value}
                        </span>
                    )}
                </div>
            );
        }

        // input (text / number)
        const isNumeric = s.setting_type === 'float' || s.setting_type === 'int';
        return (
            <div className="flex items-center gap-2">
                <input
                    type={isNumeric ? 'number' : 'text'}
                    step={s.setting_type === 'float' ? (schema.step ?? '0.01') : '1'}
                    min={schema.min_value}
                    max={schema.max_value}
                    value={val}
                    onChange={(e) => onChange(e.target.value)}
                    className={`w-28 px-2 py-1.5 rounded-lg text-base border bg-white outline-none focus:border-sb-primary font-mono ${changed ? 'border-amber-400 text-amber-600' : 'border-gray-200 text-gray-700'}`}
                />
                {changed && (
                    <span className="text-sm text-gray-400 whitespace-nowrap">기본 {s.default_value}</span>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4 w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-sb-header">시스템 설정</p>
                    <p className="text-base text-gray-400">AI 엔진 내부 파라미터를 조정합니다. 기본값을 유지하는 것을 권장합니다.</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="px-3 py-2 rounded-lg text-base border border-gray-100 bg-gray-50 text-gray-600 w-28 text-center truncate" title={updatedBy}>
                        {updatedBy || '로그인 필요'}
                    </span>
                    <button
                        onClick={handleReload}
                        disabled={reload.isLoading}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-base font-medium rounded-lg transition-colors"
                    >
                        <RefreshCw size={13} className={reload.isLoading ? 'animate-spin' : ''} />
                        엔진 재로드
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges || update.isLoading}
                        className={`flex items-center gap-1.5 px-3 py-2 text-base font-medium rounded-lg transition-colors ${hasChanges ? 'bg-sb-primary hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                    >
                        <Save size={13} />
                        {update.isLoading ? '저장 중...' : '변경 저장'}
                    </button>
                </div>
            </div>

            {/* 비개발자 주의 배너 */}
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
                <span className="text-amber-500 text-lg shrink-0">⚠️</span>
                <div>
                    <p className="text-sm font-semibold text-amber-800">이 페이지는 기술 설정 페이지입니다.</p>
                    <p className="text-sm text-amber-700">잘못 변경하면 AI 답변 품질에 직접 영향을 줍니다. 담당 개발자와 함께 검토한 후 변경하세요. 변경하지 않아도 AI는 정상 동작합니다.</p>
                </div>
            </div>

            {isLoading && <p className="text-center py-8 text-base text-gray-400">불러오는 중...</p>}

            {!isLoading && Object.entries(groups).map(([groupName, groupItems]) => (
                <div key={groupName} className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-700">{GROUP_LABELS[groupName] ?? groupName}</span>
                        <span className="text-sm text-gray-400 ml-auto">{groupItems.length}개 항목</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                        {groupItems.map((s) => {
                            const changed = (edits[s.setting_key] ?? s.setting_value) !== s.setting_value;
                            return (
                                <div key={s.setting_key} className={`px-4 py-3 flex items-center gap-4 ${changed ? 'bg-amber-50' : ''}`}>
                                    {/* 좌: 키 + 설명 */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-sb-header">{s.description || s.setting_key}</span>
                                            {changed && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 font-medium">변경됨</span>}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">기본값: {s.default_value}</p>
                                    </div>
                                    {/* 우: 입력 — 고정 너비로 모든 컨트롤 동일 열 정렬 */}
                                    <div className="shrink-0 w-52 flex items-center">
                                        {renderInput(s)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {!isLoading && items.length === 0 && (
                <p className="text-center py-8 text-base text-gray-400">설정 항목이 없습니다.</p>
            )}
        </div>
    );
}
